import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { atualizarJson, enviarJson, enviarSemConteudo, pedirJson } from "./api";

/* O sino do topo: as notificações da PESSOA (não da empresa), com a contagem do não lido.
   O contrato é o de `api/app/dominios/notificacoes/`. O sino recebe tudo e não tem interruptor;
   só o e-mail se liga e desliga. */

export const CHAVE_DAS_NOTIFICACOES = ["notificacoes"] as const;

/** A contagem se atualiza sozinha: quem fica com a aba aberta vê o sino mudar. */
export const INTERVALO_DO_SINO_EM_MS = 60_000;

const uuid = z.string().uuid();

const esquemaNotificacao = z.object({
  id: uuid,
  tipo: z.string(),
  titulo: z.string(),
  corpo: z.string().nullable(),
  link: z.string().nullable(),
  ator_nome: z.string().nullable(),
  lida: z.boolean(),
  criada_em: z.string(),
});

const esquemaDoSino = z.object({
  nao_lidas: z.number().int().nonnegative(),
  itens: z.array(esquemaNotificacao),
});

const esquemaMarcadas = z.object({ marcadas: z.number().int().nonnegative() });
const esquemaPreferencia = z.object({ email_ligado: z.boolean() });

export type Notificacao = z.infer<typeof esquemaNotificacao>;
export type DoSino = z.infer<typeof esquemaDoSino>;

/** "9+" a partir de dez: o número exato deixa de ajudar e estoura o selo. */
export function contagemLegivel(naoLidas: number): string {
  return naoLidas > 9 ? "9+" : String(naoLidas);
}

/** O texto que um leitor de tela lê no botão: o número por extenso, sem depender da cor do selo. */
export function rotuloDoSino(naoLidas: number): string {
  if (naoLidas === 0) return "Notificações, nenhuma nova";
  return `Notificações, ${naoLidas} ${naoLidas === 1 ? "nova" : "novas"}`;
}

/**
 * Só caminho da própria aplicação vira link. O servidor monta `link` a partir de ids que ele
 * mesmo conhece, mas a tela não confia: `//outro.site` ou `https://…` não são destino do sino.
 */
export function destinoSeguro(link: string | null): string | null {
  if (!link) return null;
  if (!link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  return link;
}

const RELATIVO = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

/** "agora", "há 5 minutos", "ontem", e a data curta depois de uma semana. */
export function quandoLegivel(iso: string, agora: Date = new Date()): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return "";
  const segundos = Math.round((quando.getTime() - agora.getTime()) / 1000);
  const abs = Math.abs(segundos);
  if (abs < 60) return "agora";
  if (abs < 3600) return RELATIVO.format(Math.round(segundos / 60), "minute");
  if (abs < 86_400) return RELATIVO.format(Math.round(segundos / 3600), "hour");
  if (abs < 7 * 86_400) return RELATIVO.format(Math.round(segundos / 86_400), "day");
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(quando);
}

export function useSino(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DAS_NOTIFICACOES, "lista"],
    enabled: habilitado,
    refetchInterval: INTERVALO_DO_SINO_EM_MS,
    queryFn: ({ signal }) => pedirJson("/notificacoes?limite=30", esquemaDoSino, { signal }),
  });
}

export function useMarcarComoLida() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enviarSemConteudo(`/notificacoes/${encodeURIComponent(id)}/lida`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_NOTIFICACOES }),
  });
}

export function useMarcarTodasComoLidas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => enviarJson("/notificacoes/lidas", esquemaMarcadas, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_NOTIFICACOES }),
  });
}

export function usePreferenciaDeEmail(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DAS_NOTIFICACOES, "preferencia"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/notificacoes/preferencias", esquemaPreferencia, { signal }),
  });
}

export function useDefinirEmail() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ligado: boolean) =>
      atualizarJson("/notificacoes/preferencias", esquemaPreferencia, { email_ligado: ligado }),
    // Sem `void`: a mutação só termina quando a leitura nova chega, e a caixa não pisca de volta
    // ao valor velho no intervalo entre gravar e reler.
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_DAS_NOTIFICACOES }),
  });
}
